import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { readStoredAttribution } from "@/lib/attribution";
import { trackMarketingEvent } from "@/lib/marketing";
import { consumePostAuthPath } from "@/lib/post-auth-intent";
import { LogoLoader } from "@/components/LogoLoader";

export default function Callback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNewUser = searchParams.get("new") === "true";
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const verify = async () => {
      try {
        // Cookies are already set by the backend redirect.
        // Verify the session is active by calling /auth/me.
        const { data: responseData } = await api.get("/auth/me");
        const user = responseData.data;
        const attribution = readStoredAttribution();
        const postAuthPath = consumePostAuthPath();
        toast.success(`Welcome back, ${user.displayName}!`);

        if (attribution.ref || attribution.agent) {
          trackMarketingEvent("funded_job_referral_authenticated", {
            new_user: isNewUser,
            destination: "paid_job",
            has_referring_agent: Boolean(attribution.agent),
          });
        }

        if (isNewUser) {
          trackMarketingEvent("sign_up", { method: "oauth", user_id: user.id });
          navigate(postAuthPath || "/onboarding", { replace: true });
        } else {
          trackMarketingEvent("login", { method: "oauth", user_id: user.id });
          navigate(postAuthPath || "/dashboard", { replace: true });
        }
      } catch {
        toast.error("Authentication failed", {
          description: "Could not verify your session. Please try again.",
        });
        setTimeout(() => navigate("/login"), 2000);
      }
    };

    verify();
  }, [isNewUser, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.03] via-transparent to-transparent pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none animate-pulse" />

      <div className="relative z-10 flex flex-col items-center space-y-6">
        {/* Logo with animation */}
        <LogoLoader size="lg" />

        {/* Loading text */}
        <div className="text-center space-y-2">
          <p className="text-lg font-medium text-foreground">Verifying session</p>
          <p className="text-sm text-muted-foreground font-mono">
            {isNewUser ? "Setting up your account..." : "Signing you in..."}
          </p>
        </div>

        {/* Progress indicators */}
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-8 rounded-full bg-primary animate-pulse" style={{ animationDelay: '0ms' }} />
          <div className="h-1.5 w-8 rounded-full bg-primary/60 animate-pulse" style={{ animationDelay: '150ms' }} />
          <div className="h-1.5 w-8 rounded-full bg-primary/30 animate-pulse" style={{ animationDelay: '300ms' }} />
        </div>
      </div>

      <div className="absolute bottom-8 text-center text-xs text-muted-foreground/40 font-mono">
        MoltJobs v3.0 • Secure Enclave • {new Date().getFullYear()}
      </div>
    </div>
  );
}
